import { bySubstance, pageLabel } from '../armKit';
import { cardPartCss } from '../surface';

import type { ArmContext, ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';
import type { ProfileCard } from '../surface';

type Facts = Map<number, WorkFacts>;

const FLAG = 'qualifiedabstracts';
const TOP_COUNT = 5;
const READ_SOURCE = 'profile-liautotest-qualified-read';

function marked(card: ProfileCard): boolean {
  return card.flag(FLAG) != null;
}

// A card with nothing hidden is still a top-five card: it keeps its chip and
// its action, it simply has less to reveal.
function qualify(card: ProfileCard, facts: Facts, ctx: ArmContext): void {
  card.setFlag(FLAG, 'full');
  card.expandAbstract();

  const pages = pageLabel(facts.get(card.workId)?.pageCount);
  if (pages != null) card.addChip(pages);
  if (card.url == null) return;

  const action = card.addAction('Read', card.url);
  action?.addEventListener('click', () => {
    ctx.track(READ_SOURCE, { entityId: card.workId });
  });
}

function compact(card: ProfileCard, ctx: ArmContext): void {
  const sel = card.setFlag(FLAG, 'collapsed');
  card.collapse();
  ctx.injectStyles(
    cardPartCss(
      'row',
      `${ctx.scope} ${sel}`,
      'margin-top: 4px; margin-bottom: 4px;',
    ) + cardPartCss('item', `${ctx.scope} ${sel}`, 'margin-bottom: 0;'),
  );
}

export const qualifiedAbstracts: ArmSpec = {
  onReady: async (ctx) => {
    const facts = await ctx.workFacts();
    if (facts.size === 0 || ctx.cards().length === 0) return;

    ctx.reorderCards(bySubstance((workId) => facts.get(workId)?.pageCount));

    // Read back rather than reuse a sorted array: the reorder is scoped per
    // section, so only the DOM knows which five cards ended up on top.
    const ordered = ctx.cards();
    ordered.slice(0, TOP_COUNT).forEach((card) => qualify(card, facts, ctx));
    ordered.slice(TOP_COUNT).forEach((card) => compact(card, ctx));
  },

  // Gated on the mark onReady left rather than on a fresh fetch, so an arm that
  // degraded to control stays control. A batch is only ever compacted: it is
  // never reordered under a reader who is already scrolling, and never promoted.
  onCards: (cards, ctx) => {
    if (!ctx.cards().some(marked)) return;

    cards.filter((card) => !marked(card)).forEach((card) => compact(card, ctx));
  },
};
